
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  PencilIcon,
  Trash2Icon,
  PlusCircleIcon,
  XIcon,
  ListIcon,
  TableIcon,
  GripVerticalIcon,
} from 'lucide-react';
import { useCustomVariableStore } from '@/store/customVariableStore';
import { useSystemPromptStore } from '@/store/systemPromptStore';
import { VariableForm } from './VariableForm';
import { CustomVariable } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- Presentational Component for the Row ---
interface VariableRowProps {
  variable: CustomVariable;
  displayIndex?: number;
  isDragging?: boolean;
  isOverlay?: boolean;
  onEdit?: (variable: CustomVariable) => void;
  onDelete?: (id: string) => void;
  onToggle?: (id: string, checked: boolean) => void;
  onToggleSimple?: (id: string, checked: boolean) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

function VariableRow({
  variable,
  displayIndex,
  isDragging,
  isOverlay,
  onEdit,
  onDelete,
  onToggle,
  onToggleSimple,
  dragHandleProps,
}: VariableRowProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between p-2 border rounded-md bg-card mb-2 touch-none",
        isDragging && "opacity-30",
        isOverlay && "shadow-xl border-primary/50 opacity-100 bg-card cursor-grabbing w-full"
      )}
    >
      <div className="flex items-center gap-2 flex-1 overflow-hidden">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className={cn(
            "cursor-grab hover:text-foreground text-muted-foreground p-1 rounded-sm hover:bg-muted",
            isOverlay && "cursor-grabbing"
          )}
        >
          <GripVerticalIcon className="h-4 w-4" />
        </div>

        <Badge 
          variant={displayIndex ? "secondary" : "outline"} 
          className={cn("text-[10px] h-5 w-8 flex justify-center transition-all", !displayIndex && "text-muted-foreground border-dashed opacity-50")}
        >
          {displayIndex ? `#${displayIndex}` : '-'}
        </Badge>
        
        <div className="flex flex-col min-w-0 flex-1 gap-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold">{variable.key}</span>
            
            {/* Simple Mode Toggle */}
            <div className="flex items-center gap-1.5 bg-muted/40 px-1.5 py-0.5 rounded-sm border border-transparent hover:border-border transition-colors">
                 <Checkbox 
                    id={`simple-mode-${variable.id}${isOverlay ? '-overlay' : ''}`}
                    checked={variable.is_freq_used} 
                    onCheckedChange={(checked) => onToggleSimple?.(variable.id, checked as boolean)}
                    className="h-3.5 w-3.5"
                 />
                 <label 
                   htmlFor={`simple-mode-${variable.id}${isOverlay ? '-overlay' : ''}`} 
                   className="text-[10px] font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground"
                 >
                   Simple
                 </label>
            </div>
          </div>
          <span className="text-xs text-muted-foreground truncate">
            {variable.value}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-2">
        <Switch
          checked={variable.enabled}
          onCheckedChange={(checked) => onToggle?.(variable.id, checked)}
          className="scale-75"
        />
        <div className="h-4 w-px bg-border mx-1" />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit?.(variable)}
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={() => onDelete?.(variable.id)}
        >
          <Trash2Icon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// --- Sortable Item Wrapper ---
interface SortableItemProps {
  variable: CustomVariable;
  displayIndex?: number;
  onEdit: (variable: CustomVariable) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, checked: boolean) => void;
  onToggleSimple: (id: string, checked: boolean) => void;
}

function SortableItem({ variable, displayIndex, onEdit, onDelete, onToggle, onToggleSimple }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: variable.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <VariableRow
        variable={variable}
        displayIndex={displayIndex}
        isDragging={isDragging}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggle={onToggle}
        onToggleSimple={onToggleSimple}
        dragHandleProps={listeners}
      />
    </div>
  );
}

// --- Main Modal Component ---
export function VariableToggleModal() {
  const {
    isModalOpen,
    closeModal,
    variables,
    toggleVariable,
    isEditMode,
    exitEditMode,
    editingVariable,
    setEditingVariable,
    deleteVariable,
    reorderVariables,
  } = useCustomVariableStore();

  const { systemPrompts, fetchSystemPrompts } = useSystemPromptStore();

  const [viewMode, setViewMode] = useState<'simple' | 'details'>('simple');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (isModalOpen && systemPrompts.length === 0) {
      fetchSystemPrompts();
    }
  }, [isModalOpen, systemPrompts.length, fetchSystemPrompts]);

  const handleClose = () => {
    closeModal();
    setViewMode('simple');
    if (isEditMode) exitEditMode();
  };

  const handleEditClick = (variable: CustomVariable) => {
    setEditingVariable(variable);
    setIsFormOpen(true);
  };

  const handleCreateClick = () => {
    setEditingVariable(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingVariable(null);
  };

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = variables.findIndex((v) => v.id === active.id);
      const newIndex = variables.findIndex((v) => v.id === over.id);
      
      const newOrder = arrayMove(variables, oldIndex, newIndex);
      const orderedIds = newOrder.map(v => v.id);
      
      reorderVariables(orderedIds);
    }
    setActiveId(null);
  };

  const sortedVariables = [...variables].sort((a, b) => a.order_number - b.order_number);

  let simpleSeq = 0;
  const simpleIndexMap = new Map<string, number>();
  sortedVariables.forEach(v => {
    if (v.is_freq_used) {
      simpleSeq++;
      simpleIndexMap.set(v.id, simpleSeq);
    }
  });

  const displayedSimpleVariables = sortedVariables.filter((v) => v.is_freq_used);
  
  const activeVariable = activeId ? variables.find(v => v.id === activeId) : null;

  const renderSimpleMode = () => (
    <div className="grid gap-3 py-4">
      {displayedSimpleVariables.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-8">
          No frequently used variables set. Switch to Details mode to configure.
        </div>
      ) : (
        displayedSimpleVariables.map((variable, index) => (
          <div
            key={variable.id}
            className="flex items-center justify-between p-2 rounded-md hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0 text-xs font-mono">
                {index + 1}
              </Badge>
              <div className="flex flex-col">
                <span className="font-mono text-sm font-medium">{variable.key}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {variable.value}
                </span>
              </div>
            </div>
            <Switch
              checked={variable.enabled}
              onCheckedChange={(checked) =>
                toggleVariable(variable.id, { enabled: checked })
              }
            />
          </div>
        ))
      )}
    </div>
  );

  const renderDetailsMode = () => (
    <div className="py-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-muted-foreground">All Variables</h3>
        <Button variant="outline" size="sm" onClick={handleCreateClick}>
          <PlusCircleIcon className="mr-2 h-3.5 w-3.5" />
          New Variable
        </Button>
      </div>
      
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedVariables.map(v => v.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedVariables.map((variable) => (
              <SortableItem
                key={variable.id}
                variable={variable}
                displayIndex={simpleIndexMap.get(variable.id)}
                onEdit={handleEditClick}
                onDelete={deleteVariable}
                onToggle={(id, checked) => toggleVariable(id, { enabled: checked })}
                onToggleSimple={(id, checked) => toggleVariable(id, { is_freq_used: checked })}
              />
            ))}
          </SortableContext>
          
          {mounted && createPortal(
            <DragOverlay>
              {activeVariable ? (
                <div className="w-[var(--radix-dialog-content-width)] max-w-[600px]">
                   <VariableRow 
                     variable={activeVariable} 
                     displayIndex={simpleIndexMap.get(activeVariable.id)}
                     isOverlay 
                     onToggle={() => {}}
                     onToggleSimple={() => {}}
                   />
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        open={isModalOpen && !isFormOpen}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
      >
        <DialogContent showCloseButton={false} className={cn(
          "transition-all duration-200",
          viewMode === 'details' ? "sm:max-w-[600px]" : "sm:max-w-[425px]"
        )}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>Variables</DialogTitle>
              <div className="flex bg-muted rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('simple')}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all",
                    viewMode === 'simple' 
                      ? "bg-background shadow-sm text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <ListIcon className="w-3.5 h-3.5" />
                  Simple
                </button>
                <button
                  onClick={() => setViewMode('details')}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all",
                    viewMode === 'details' 
                      ? "bg-background shadow-sm text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <TableIcon className="w-3.5 h-3.5" />
                  Details
                </button>
              </div>
            </div>
            <DialogDescription>
              {viewMode === 'simple'
                ? 'Quickly toggle frequently used variables.'
                : 'Manage all variables, drag to reorder.'}
            </DialogDescription>
          </DialogHeader>

          {viewMode === 'simple' ? renderSimpleMode() : renderDetailsMode()}

          <DialogFooter>
            <Button variant="secondary" onClick={handleClose} className="w-full sm:w-auto">
              <XIcon className="mr-2 h-4 w-4" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <VariableForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        variable={editingVariable}
      />
    </>
  );
}